import React, { useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Tabs, Tab, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  Grid, Tooltip as MuiTooltip, Stack, Alert, useTheme, Accordion, AccordionSummary, AccordionDetails, Button, IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import {
  ResponsiveContainer, ComposedChart, LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell, Legend,
} from 'recharts';
import { BacktestResultJson, TfReportJson, ComboResult, TradeRow } from '../../services/backtest.service';
import { useTabs } from '../../contexts/TabsContext';
import { describeRule, OVERVIEW_EXPLAINER } from './backtestHelp';

const GREEN = '#26a69a', RED = '#ef5350', BLUE = '#42a5f5', GREY = '#9e9e9e';
const tfLabel = (m: number) => (m >= 1440 ? `${m / 1440}D` : m >= 60 ? `${m / 60}H` : `${m}m`);
const pct = (x?: number, dp = 1) => (x == null || !isFinite(x) ? '—' : `${x.toFixed(dp)}%`);
const num = (x?: number, dp = 2) => (x == null || !isFinite(x) ? '—' : x.toFixed(dp));
const money = (x?: number) => (x == null ? '—' : `₹${Math.round(x).toLocaleString('en-IN')}`);
const fmtDate = (t: number) => new Date(t * 1000).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
const fmtDateTime = (t: number) => new Date(t * 1000).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });

const Stat: React.FC<{ label: string; value: string; hint?: string; color?: string }> = ({ label, value, hint, color }) => (
  <MuiTooltip title={hint || ''} arrow>
    <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
      <Typography variant="body2" fontWeight={800} sx={{ color }} noWrap>{value}</Typography>
    </Box>
  </MuiTooltip>
);

const ChartCard: React.FC<{ title: string; children: React.ReactNode; height?: number }> = ({ title, children, height = 240 }) => (
  <Card variant="outlined">
    <CardContent sx={{ pb: '8px !important' }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>{title}</Typography>
      <Box sx={{ height }}>
        <ResponsiveContainer width="100%" height="100%">{children as any}</ResponsiveContainer>
      </Box>
    </CardContent>
  </Card>
);

function histogram(returns: number[], bins = 21): Array<{ bucket: string; count: number; mid: number }> {
  if (!returns.length) return [];
  const lo = Math.min(...returns), hi = Math.max(...returns);
  const span = hi - lo || 1; const w = span / bins;
  const out = new Array(bins).fill(0).map((_, i) => ({ bucket: (lo + (i + 0.5) * w).toFixed(1), count: 0, mid: lo + (i + 0.5) * w }));
  for (const r of returns) { let idx = Math.floor((r - lo) / w); if (idx >= bins) idx = bins - 1; if (idx < 0) idx = 0; out[idx].count++; }
  return out;
}

const ComboCharts: React.FC<{ combo: ComboResult }> = ({ combo }) => {
  const theme = useTheme();
  const m = combo.metrics;
  const equityData = useMemo(() => (combo.equityCurve || []).map((p, i) => ({
    t: p.t, date: fmtDate(p.t), equity: p.equity, buyhold: combo.buyHoldCurve?.[i]?.equity ?? null, dd: p.dd,
  })), [combo]);
  const hist = useMemo(() => histogram(combo.tradeReturns || []), [combo]);

  return (
    <Stack spacing={1.5}>
      <Grid container spacing={1}>
        {[
          ['Net P&L', money(m.totalNetAmt), 'Sum of trade P&L (fixed amount/trade)', (m.totalNetAmt ?? 0) >= 0 ? GREEN : RED],
          ['Expectancy', `${num(m.expectancyPct)}%`, 'Avg net % per trade (after costs)', m.expectancyPct >= 0 ? GREEN : RED],
          ['Profit factor', num(m.profitFactor), 'Gross wins ÷ gross losses', m.profitFactor >= 1 ? GREEN : RED],
          ['Win rate', pct(m.winRate * 100), 'Share of winning trades'],
          ['Max drawdown', pct(m.maxDrawdownPct), 'Worst peak-to-trough on equity', RED],
          ['Sharpe', num(m.sharpe), 'Risk-adjusted return (annualised)'],
          ['Sortino', num(m.sortino), 'Downside risk-adjusted'],
          ['Annualised', pct(m.cagrPct), 'Return on capital per year'],
          ['Avg hold', `${num(m.avgDays, 1)}d`, 'Average days in trade'],
          ['Exposure', pct(m.exposurePct * 100), 'Time in market'],
          ['Trades', String(m.trades), `${m.longTrades}L / ${m.shortTrades}S`],
          ['%Good≤14d', pct(m.pctGoodWithin * 100), 'Trades hitting target within 14 days'],
        ].map(([l, v, h, c]) => (
          <Grid key={l as string} size={{ xs: 6, sm: 3, md: 2 }}><Stat label={l as string} value={v as string} hint={h as string} color={c as string} /></Grid>
        ))}
      </Grid>

      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <ChartCard title="Equity curve — strategy vs buy & hold" height={260}>
            <LineChart data={equityData} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
              <YAxis tick={{ fontSize: 10 }} width={54} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => money(v)} labelStyle={{ color: theme.palette.text.primary }} contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="equity" name="Strategy" stroke={GREEN} dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="buyhold" name="Buy & Hold" stroke={GREY} dot={false} strokeWidth={1.5} strokeDasharray="4 3" />
            </LineChart>
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <ChartCard title="Drawdown (underwater)" height={260}>
            <AreaChart data={equityData} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
              <YAxis tick={{ fontSize: 10 }} width={40} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: any) => `${num(v)}%`} contentStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="dd" name="Drawdown" stroke={RED} fill={RED} fillOpacity={0.25} />
            </AreaChart>
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 7 }}>
          <ChartCard title="Per-symbol net % (winners vs laggards)">
            <BarChart data={(combo.perSymbol || [])} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="symbol" tick={{ fontSize: 9 }} interval={0} angle={-60} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10 }} width={40} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: any) => `${num(v)}%`} contentStyle={{ fontSize: 12 }} />
              <ReferenceLine y={0} stroke={GREY} />
              <Bar dataKey="netPct" name="Net %">
                {(combo.perSymbol || []).map((s, i) => <Cell key={i} fill={s.netPct >= 0 ? GREEN : RED} />)}
              </Bar>
            </BarChart>
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <ChartCard title="Monthly returns (% of capital)">
            <BarChart data={(combo.monthly || [])} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="ym" tick={{ fontSize: 9 }} minTickGap={20} />
              <YAxis tick={{ fontSize: 10 }} width={40} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: any) => `${num(v)}%`} contentStyle={{ fontSize: 12 }} />
              <ReferenceLine y={0} stroke={GREY} />
              <Bar dataKey="pct" name="Monthly %">
                {(combo.monthly || []).map((s, i) => <Cell key={i} fill={s.pct >= 0 ? GREEN : RED} />)}
              </Bar>
            </BarChart>
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 7 }}>
          <ChartCard title="Trade-return distribution (net %)">
            <BarChart data={hist} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="bucket" tick={{ fontSize: 9 }} minTickGap={10} />
              <YAxis tick={{ fontSize: 10 }} width={36} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <ReferenceLine x="0.0" stroke={GREY} />
              <Bar dataKey="count" name="Trades">
                {hist.map((h, i) => <Cell key={i} fill={h.mid >= 0 ? GREEN : RED} />)}
              </Bar>
            </BarChart>
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Out-of-sample (train {Math.round(70)}/test 30)</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Stat label="Train exp" value={`${num(combo.oos.trainExpectancy)}%`} hint="Optimised on the earlier 70%" color={combo.oos.trainExpectancy >= 0 ? GREEN : RED} />
                <Stat label="Test exp" value={`${num(combo.oos.testExpectancy)}%`} hint="Held-out later 30% (unseen)" color={combo.oos.testExpectancy >= 0 ? GREEN : RED} />
                <Stat label="Test PF" value={num(combo.oos.testProfitFactor)} hint="Profit factor on the test slice" />
              </Stack>
              {combo.oos.degraded
                ? <Alert severity="warning" sx={{ py: 0 }}>Out-of-sample decay — possible overfit. Validate forward.</Alert>
                : <Alert severity="success" sx={{ py: 0 }}>Holds up out-of-sample (no major decay).</Alert>}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Long edge {num(m.longExpectancyPct)}% · Short edge {num(m.shortExpectancyPct)}% · vs Buy&Hold {money(m.vsBuyHoldNetAmt)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
};

const StrategyRules: React.FC<{ combo: ComboResult }> = ({ combo }) => {
  const d = describeRule(combo);
  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: '12px !important' }}>
        <Typography variant="subtitle2" fontWeight={800} gutterBottom>📋 Trading rules used (selected combination)</Typography>
        <Typography variant="caption" color="text.secondary"><code>{combo.id}</code></Typography>
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <Box><Chip size="small" label="ENTRY" color="success" sx={{ height: 18, fontSize: '0.6rem', mr: 1 }} /><Typography variant="body2" component="span" color="text.secondary">{d.entry}</Typography></Box>
          <Box><Chip size="small" label="EXIT" color="error" sx={{ height: 18, fontSize: '0.6rem', mr: 1 }} /><Typography variant="body2" component="span" color="text.secondary">{d.exit}</Typography></Box>
        </Stack>
        <Accordion disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 0, px: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
            <Typography variant="caption" fontWeight={700} color="primary">What is the trend score?</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}><Typography variant="caption" color="text.secondary">{OVERVIEW_EXPLAINER}</Typography></AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

const reasonLabel: Record<string, string> = { rule: 'Rule exit', timeStop: 'Time stop', trail: 'Trailing stop', eod: 'End of data', 'rule/eod': 'Rule (EOD)' };

const TradesSection: React.FC<{ combo: ComboResult; tf: number; openChart: (s: string, e: string, q: Record<string, string>) => void }> = ({ combo, tf, openChart }) => {
  const trades = combo.trades || [];
  const bySym = useMemo(() => {
    const m = new Map<string, TradeRow[]>();
    for (const t of trades) { if (!m.has(t.symbol)) m.set(t.symbol, []); m.get(t.symbol)!.push(t); }
    return Array.from(m.entries()).map(([symbol, ts]) => ({
      symbol, ts, net: ts.reduce((a, t) => a + t.netRetPct, 0),
      wins: ts.filter(t => t.netRetPct > 0).length,
    })).sort((a, b) => b.net - a.net);
  }, [trades]);

  if (!combo.trades) return (
    <Card variant="outlined"><CardContent><Typography variant="subtitle2" fontWeight={700} gutterBottom>Trades</Typography>
      <Alert severity="info">The full trade log is attached to the <b>recommended</b> combination. Select it (top of the All-combinations table) to inspect every trade.</Alert>
    </CardContent></Card>
  );

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: '8px !important' }}>
        <Typography variant="subtitle2" fontWeight={800} gutterBottom>
          Trades — {trades.length} total, grouped by symbol {combo.tradesTruncated && <Chip size="small" color="warning" label="truncated" sx={{ height: 16, fontSize: '0.6rem' }} />}
        </Typography>
        <Typography variant="caption" color="text.secondary">Click the chart icon on any trade to open that symbol on the Charts page at the entry bar (replay) and judge the trade.</Typography>
        <Box sx={{ mt: 1 }}>
          {bySym.map(({ symbol, ts, net, wins }) => (
            <Accordion key={symbol} disableGutters sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 0.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                  <Typography variant="body2" fontWeight={800}>{symbol}</Typography>
                  <Chip size="small" label={`${ts.length} trades`} sx={{ height: 18, fontSize: '0.62rem' }} />
                  <Typography variant="caption" sx={{ color: net >= 0 ? GREEN : RED, fontWeight: 700 }}>{net >= 0 ? '+' : ''}{num(net)}%</Typography>
                  <Typography variant="caption" color="text.secondary">win {pct(ts.length ? (wins / ts.length) * 100 : 0, 0)}</Typography>
                  <Box sx={{ flex: 1 }} />
                  <MuiTooltip title="Open this symbol on the chart" arrow>
                    <IconButton size="small" component="span" onClick={(e) => { e.stopPropagation(); openChart(symbol, ts[0]?.exchange || 'DELTA', { interval: String(tf) }); }}>
                      <CandlestickChartIcon fontSize="small" />
                    </IconButton>
                  </MuiTooltip>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {[['Side', ''], ['Entry time', 'When the position opened (UTC)'], ['Entry', 'Fill price'], ['Exit time', ''], ['Exit', ''], ['Net %', 'Profit after costs'], ['Days', 'Holding period'], ['MFE %', 'Best unrealised gain reached'], ['MAE %', 'Worst unrealised loss reached'], ['Exit', 'Why it closed'], ['', '']].map(([h, hint], i) =>
                          <MuiTooltip key={i} title={hint as string} arrow><TableCell sx={{ fontSize: '0.68rem', fontWeight: 700 }}>{h as string}</TableCell></MuiTooltip>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ts.map((t, i) => (
                        <TableRow key={i} hover>
                          <TableCell><Chip size="small" label={t.side} sx={{ height: 16, fontSize: '0.58rem', color: '#fff', bgcolor: t.side === 'LONG' ? GREEN : RED }} /></TableCell>
                          <TableCell sx={{ fontSize: '0.68rem' }}>{fmtDateTime(t.entryTime)}</TableCell>
                          <TableCell sx={{ fontSize: '0.68rem' }}>{t.entryPrice}</TableCell>
                          <TableCell sx={{ fontSize: '0.68rem' }}>{fmtDateTime(t.exitTime)}</TableCell>
                          <TableCell sx={{ fontSize: '0.68rem' }}>{t.exitPrice}</TableCell>
                          <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: t.netRetPct >= 0 ? GREEN : RED }}>{t.netRetPct >= 0 ? '+' : ''}{num(t.netRetPct)}</TableCell>
                          <TableCell sx={{ fontSize: '0.68rem' }}>{num(t.daysHeld, 1)}</TableCell>
                          <TableCell sx={{ fontSize: '0.68rem', color: GREEN }}>{num(t.mfePct)}</TableCell>
                          <TableCell sx={{ fontSize: '0.68rem', color: RED }}>{num(t.maePct)}</TableCell>
                          <TableCell sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>{reasonLabel[t.exitReason] || t.exitReason}</TableCell>
                          <TableCell>
                            <MuiTooltip title="Open this trade on the chart (replay at the entry bar)" arrow>
                              <IconButton size="small" onClick={() => openChart(t.symbol, t.exchange, { interval: String(tf), focusAt: new Date(t.entryTime * 1000).toISOString() })}>
                                <CandlestickChartIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </MuiTooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const TfReport: React.FC<{ rep: TfReportJson }> = ({ rep }) => {
  const { openChartTab } = useTabs();
  const [selId, setSelId] = useState<string | null>(rep.recommended?.id || rep.ranked[0]?.id || null);
  const combos = rep.ranked;
  // Prefer the recommended object when it's the selection — it carries the full trade log.
  const selected = (selId && rep.recommended && selId === rep.recommended.id)
    ? rep.recommended
    : (combos.find(c => c.id === selId) || rep.recommended || combos[0] || null);
  if (!selected) return <Alert severity="info">No usable data for {tfLabel(rep.tf)} — symbols lacked enough history to warm up the score.</Alert>;

  return (
    <Stack spacing={1.5}>
      <Card sx={{ borderLeft: `4px solid ${BLUE}` }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Typography variant="h6" fontWeight={800}>Recommended: <code>{rep.recommended?.id || '—'}</code></Typography>
            <Chip size="small" label={`${rep.symbolsUsed} symbols · median ${Math.round(rep.medianSpanDays)}d · buy&hold ${pct(rep.buyHoldAvgPct)}`} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}
            dangerouslySetInnerHTML={{ __html: (rep.whyRanked || '').replace(/\*\*(.+?)\*\*/g, '<b>$1</b>') }} />
          {rep.caveats?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {rep.caveats.map((c, i) => <Alert key={i} severity="warning" sx={{ py: 0, mb: 0.5 }}>{c}</Alert>)}
            </Box>
          )}
        </CardContent>
      </Card>

      {selected && <StrategyRules combo={selected} />}

      {selected && <ComboCharts combo={selected} />}

      {selected && <TradesSection combo={selected} tf={rep.tf} openChart={(s, e, q) => openChartTab(s, e, q)} />}

      <Card variant="outlined">
        <CardContent sx={{ pb: '8px !important' }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>All combinations (click a row to view its charts)</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {([
                    ['#', 'Rank by robustness'],
                    ['Combination', 'Entry rule × exit rule and its parameters'],
                    ['Exp%/trade', 'Average net profit per trade after costs — the key edge metric'],
                    ['PF', 'Profit factor = gross wins ÷ gross losses (>1 = profitable)'],
                    ['Win%', 'Share of winning trades (low is normal for trend-following)'],
                    ['MaxDD%', 'Worst peak-to-trough equity drop'],
                    ['Sharpe', 'Return per unit of volatility (higher = smoother)'],
                    ['AvgDays', 'Average holding period in days'],
                    ['%Sym+', 'Share of symbols profitable (breadth)'],
                    ['OOS test', 'Expectancy on held-out (unseen) data; ⚠ = decayed vs train'],
                    ['Robust', 'Composite ranking score (breadth × consistency × sample)'],
                  ] as [string, string][]).map(([h, hint]) => <MuiTooltip key={h} title={hint} arrow><TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell></MuiTooltip>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {rep.baseline && (
                  <TableRow sx={{ bgcolor: 'action.selected' }}>
                    <TableCell>⚙️</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem' }}>{rep.baseline.id} <i>(current default)</i></TableCell>
                    <TableCell>{num(rep.baseline.metrics.expectancyPct)}</TableCell>
                    <TableCell>{num(rep.baseline.metrics.profitFactor)}</TableCell>
                    <TableCell>{pct(rep.baseline.metrics.winRate * 100)}</TableCell>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
                {combos.map((c, i) => (
                  <TableRow key={c.id} hover selected={c.id === selId} sx={{ cursor: 'pointer' }} onClick={() => setSelId(c.id)}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.id}</TableCell>
                    <TableCell sx={{ color: c.metrics.expectancyPct >= 0 ? GREEN : RED, fontWeight: 700 }}>{num(c.metrics.expectancyPct)}</TableCell>
                    <TableCell>{num(c.metrics.profitFactor)}</TableCell>
                    <TableCell>{pct(c.metrics.winRate * 100)}</TableCell>
                    <TableCell>{c.metrics.maxDrawdownPct != null ? pct(c.metrics.maxDrawdownPct) : '—'}</TableCell>
                    <TableCell>{c.metrics.sharpe != null ? num(c.metrics.sharpe) : '—'}</TableCell>
                    <TableCell>{num(c.metrics.avgDays, 1)}</TableCell>
                    <TableCell>{pct(c.fracPosSymbols * 100, 0)}</TableCell>
                    <TableCell sx={{ color: c.oos.testExpectancy >= 0 ? GREEN : RED }}>{num(c.oos.testExpectancy)}{c.oos.degraded ? ' ⚠' : ''}</TableCell>
                    <TableCell>{num(c.robustness, 3)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
};

const BacktestResults: React.FC<{ result: BacktestResultJson }> = ({ result }) => {
  const [tab, setTab] = useState(0);
  const tfs = result.perTf || [];
  if (!tfs.length) return <Alert severity="info">No results.</Alert>;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {tfs.map((t, i) => <Tab key={t.tf} label={`${tfLabel(t.tf)} → ${tfLabel(t.htfMin)} HTF`} value={i} />)}
        </Tabs>
        <Typography variant="caption" color="text.secondary">
          {result.source} · {result.symbolCount} symbols · ₹{result.fixedAmount.toLocaleString('en-IN')}/trade · {result.preset} · cost {result.costModel?.feePctPerSide}%+{result.costModel?.slippageBpsPerSide}bps/side
        </Typography>
      </Box>
      {tfs[tab] && <TfReport rep={tfs[tab]} />}
    </Box>
  );
};

export default BacktestResults;
